import IPPageNumber from "app/pages/InteractiveProposal/InteractiveProposalDownload/IPPageNuber";
import HexagonImg from "app/pages/InteractiveProposal/images/hexagonImg.png";
import { getSessionStore } from "app/utils/sessionStore";
import { getThemeColors } from "app/utils/utils";
import { getColorVariations } from "app/utils/colorUtils";
import { PFSummaryAppendixJson } from "app/Data/PFSummaryAppendixJson";
import { SDAppendixJson, headerColumnGroupSDAppendix } from "app/Data/SDAppendixJson";

interface IRCSDAppendixPageProps {
  summary: any[];
  appendixRows: any[];
  insuranceCompanyName?: string;
  pageNumber: string | number;
  isFirstPage?: boolean;
  rowOffset?: number;
}

const groupKeys = Object.keys(headerColumnGroupSDAppendix);
const allGroups = groupKeys.map((key) => ({
  label: key,
  fields: headerColumnGroupSDAppendix[key as keyof typeof headerColumnGroupSDAppendix],
}));

const filteredGroups = allGroups
  .map((g) => ({ ...g, fields: g.fields.filter((f) => SDAppendixJson.some((c) => c.field === f)) }))
  .filter((g) => g.fields.length > 0);

const orderedAppendixColumns = filteredGroups.flatMap((g) =>
  g.fields.map((field) => SDAppendixJson.find((c) => c.field === field)!).filter(Boolean),
);

const IRCSDAppendixPage = ({
  summary,
  appendixRows,
  insuranceCompanyName,
  pageNumber,
  isFirstPage = true,
  rowOffset = 0,
}: IRCSDAppendixPageProps) => {
  const { branding } = getSessionStore("lookUpResponse");
  const { secondaryColor } = getThemeColors(branding);
  const summaryColors = getColorVariations(secondaryColor, PFSummaryAppendixJson.length).reverse();
  const appendixGroupColors = getColorVariations(secondaryColor, filteredGroups.length).reverse();

  const appendixFieldColorMap: Record<string, string> = {};
  filteredGroups.forEach((g, gi) => {
    g.fields.forEach((f) => {
      appendixFieldColorMap[f] = appendixGroupColors[gi];
    });
  });

  const summaryRow = summary?.[0] ?? {};

  return (
    <div className="flex flex-col h-full relative min-h-screen">
      <div className="relative z-20 px-[2em] w-full max-w-[56em] mx-auto p-[1.75em]">
        {isFirstPage && (
          <>
            <div className="text-xl font-bold mb-3 text-center interactive-proposal-text conthrax-font">
              Appendix
            </div>
            {insuranceCompanyName && (
              <p className="mb-3 text-base conthrax-font" style={{ color: "#495057" }}>
                {insuranceCompanyName} – Please see full insurance company illustration
              </p>
            )}

            {/* Summary table */}
            <p className="text-xs font-semibold mb-1 conthrax-font" style={{ color: "#495057" }}>
              Summary
            </p>
            <table className="w-full border-collapse mb-4">
              <thead className="text-white">
                <tr>
                  {PFSummaryAppendixJson.map((col, ci) => (
                    <th
                      key={ci}
                      className="p-1 text-center pdf_table_border border-white font-semibold"
                      style={{
                        backgroundColor: summaryColors[ci % summaryColors.length],
                        fontSize: "7px",
                      }}
                    >
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr style={{ backgroundColor: "#EEEEEE", color: "#000000", fontSize: "7px" }}>
                  {PFSummaryAppendixJson.map((col, ci) => {
                    const value = summaryRow[col.field];
                    const formatted = col.formatter ? col.formatter(value) : value ?? "—";
                    return (
                      <td key={ci} className="p-1 text-center pdf_table_border border-white">
                        {formatted}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>

            <p className="text-xs font-semibold mb-1 conthrax-font" style={{ color: "#495057" }}>
              Plan Illustration
            </p>
          </>
        )}

        {/* Appendix table */}
        <table className="w-full border-collapse">
          <thead className="text-white">
            <tr>
              {orderedAppendixColumns.map((col, ci) => (
                <th
                  key={ci}
                  className="p-1 text-center pdf_table_border border-white font-semibold"
                  style={{ backgroundColor: appendixFieldColorMap[col.field], fontSize: "7px" }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {appendixRows.map((row, i) => (
              <tr
                key={i}
                style={{
                  backgroundColor: (i + rowOffset) % 2 === 0 ? "#EEEEEE" : "#FFFFFF",
                  color: "#000000",
                  fontSize: "7px",
                }}
              >
                {orderedAppendixColumns.map((col, ci) => {
                  const value = row[col.field];
                  const formatted = col.formatter ? col.formatter(value) : value ?? "—";
                  return (
                    <td key={ci} className="p-1 text-center pdf_table_border border-white">
                      {formatted}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="absolute bottom-0 left-0 w-full">
        <div
          className="h-[350px] bg-cover bg-center"
          style={{ backgroundImage: `url(${HexagonImg})` }}
        />
        <div className="px-[4em] w-full max-w-[56em] mx-auto p-[1.75em] pt-1">
          <IPPageNumber number={pageNumber} />
        </div>
      </div>
    </div>
  );
};

export default IRCSDAppendixPage;
